import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Image, PlusCircle, X } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateRoomCategoryFormData,
  createRoomCategorySchema,
} from "./utils/categorySchema";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FormField } from "./guests/components/form/FormFields";
import ImageSelector from "./guests/components/form/ImageSelector";
import { useHotel } from "@/hooks/useHotel";
import { useCategoryCreate } from "./rooms/hooks/useCategoryCreate";
import { useRoomCategoriesFiltersSettings } from "@/hooks/useRoomCategories";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_CUSTOM_FEATURE_ICON,
  DEFAULT_ROOM_FEATURE_ICONS,
  DEFAULT_ROOM_FEATURES,
  ROOM_FEATURE_ICON_OPTIONS,
  RoomFeatureIcon,
  getDefaultFeatureIcon,
  normalizeFeatureName,
  pickAmenityIcons,
  uniqueFeatureNames,
} from "./utils/roomFeatureOptions";

export const AddCategoryPage = () => {
  const navigate = useNavigate();
  const { hotelId } = useHotel();
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const { handleRoomCategorySubmit } = useCategoryCreate();
  const { data: filtersSettings } = useRoomCategoriesFiltersSettings(
    hotelId ?? "",
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    control,
  } = useForm<CreateRoomCategoryFormData>({
    resolver: zodResolver(createRoomCategorySchema),
    defaultValues: {
      name: "",
      description: "",
      amenities: [],
      amenityIcons: {},
      price: "0",
      customFeatures: [],
    },
  });

  const { append, remove, fields } = useFieldArray({
    control,
    name: "customFeatures",
  });

  const customFeatures = watch("customFeatures") ?? [];
  const amenities = watch("amenities") ?? [];
  const watchedAmenityIcons = watch("amenityIcons");
  const selectedImage = watch("photoId");
  const amenityIcons = useMemo(
    () => watchedAmenityIcons ?? {},
    [watchedAmenityIcons],
  );

  const availableFeatures = useMemo(
    () =>
      uniqueFeatureNames([
        ...DEFAULT_ROOM_FEATURES,
        ...(filtersSettings?.amenities ?? []),
      ]),
    [filtersSettings],
  );

  const featureIconMap = useMemo(
    () => ({
      ...DEFAULT_ROOM_FEATURE_ICONS,
      ...(filtersSettings?.amenityIcons ?? {}),
      ...amenityIcons,
    }),
    [amenityIcons, filtersSettings],
  );

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const setFeatureIcon = (feature: string, icon: string) => {
    const normalizedFeature = normalizeFeatureName(feature);

    if (!normalizedFeature) {
      return;
    }

    setValue(
      "amenityIcons",
      { ...amenityIcons, [normalizedFeature]: icon },
      { shouldDirty: true },
    );
  };

  const setCustomFeatureIcon = (index: number, icon: string) => {
    const feature = normalizeFeatureName(customFeatures[index]?.value ?? "");

    setValue(`customFeatures.${index}.icon`, icon, { shouldDirty: true });

    if (feature) {
      setFeatureIcon(feature, icon);
    }
  };

  const handleCustomFeatureNameChange = (index: number, value: string) => {
    const previousFeature = normalizeFeatureName(
      customFeatures[index]?.value ?? "",
    );
    const nextFeature = normalizeFeatureName(value);
    const currentIcon =
      customFeatures[index]?.icon ??
      amenityIcons[previousFeature] ??
      DEFAULT_CUSTOM_FEATURE_ICON;

    setValue(`customFeatures.${index}.value`, value, { shouldDirty: true });

    if (previousFeature === nextFeature) {
      return;
    }

    const updatedAmenities = uniqueFeatureNames(
      amenities
        .map((amenity) => (amenity === previousFeature ? nextFeature : amenity))
        .filter(Boolean),
    );

    setValue("amenities", updatedAmenities, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const updatedIcons = { ...amenityIcons };

    if (previousFeature) {
      delete updatedIcons[previousFeature];
    }

    if (nextFeature) {
      updatedIcons[nextFeature] = currentIcon;
    }

    setValue("amenityIcons", updatedIcons, { shouldDirty: true });
  };

  const toggleFeature = (feature: string) => {
    const normalizedFeature = normalizeFeatureName(feature);

    if (!normalizedFeature) {
      return;
    }

    const exists = amenities.includes(normalizedFeature);
    const updated = exists
      ? amenities.filter((amenity) => amenity !== normalizedFeature)
      : uniqueFeatureNames([...amenities, normalizedFeature]);

    setValue("amenities", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!exists) {
      setFeatureIcon(
        normalizedFeature,
        featureIconMap[normalizedFeature] ?? getDefaultFeatureIcon(feature),
      );
    }
  };

  const removeCustomFeature = (index: number) => {
    const feature = normalizeFeatureName(customFeatures[index]?.value ?? "");

    setValue(
      "amenities",
      amenities.filter((amenity) => amenity !== feature),
      { shouldDirty: true, shouldValidate: true },
    );

    if (feature) {
      const updatedIcons = { ...amenityIcons };

      delete updatedIcons[feature];
      setValue("amenityIcons", updatedIcons, { shouldDirty: true });
    }

    remove(index);
  };

  const renderIconSelect = (
    feature: string,
    selectedIcon: string,
    onIconChange: (icon: string) => void,
  ) => {
    return (
      <Select value={selectedIcon} onValueChange={onIconChange}>
        <SelectTrigger size="sm" className="w-[180px] justify-between">
          <span className="flex min-w-0 items-center gap-2">
            <RoomFeatureIcon icon={selectedIcon} />
            <SelectValue placeholder="Select icon" />
          </span>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {ROOM_FEATURE_ICON_OPTIONS.map((option) => (
              <SelectItem
                key={`${feature}-${option.value}`}
                value={option.value}
              >
                <span className="flex items-center gap-2">
                  <RoomFeatureIcon icon={option.value} />
                  {option.label}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  };

  const onSubmit = async (data: CreateRoomCategoryFormData) => {
    const selectedAmenities = uniqueFeatureNames(data.amenities);
    const request = {
      name: data.name,
      description: data.description,
      amenities: selectedAmenities,
      amenityIcons: pickAmenityIcons(selectedAmenities, data.amenityIcons),
      price: data.price,
    };

    await handleRoomCategorySubmit(request, hotelId!, selectedPhotoFile);
    navigate("/reservations/rooms/categories");
  };

  return (
    <div className="relative min-h-screen p-6">
      <Button
        variant="new_outline"
        size="sm"
        onClick={() => navigate("/reservations/rooms/categories")}
        className="absolute top-12 left-12"
      >
        <ArrowLeft />
        Back
      </Button>

      <div className="mx-auto mt-12 max-w-3xl">
        <h1 className="mb-4 text-center text-2xl font-semibold">
          Add Category
        </h1>

        <ImageSelector
          defaultIcon={<Image className="size-[72px] stroke-1" />}
          overlayContent={
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white">
              <PlusCircle className="size-5" />
            </div>
          }
          onImageSelect={handleImageUpload}
          selectedImage={selectedImage}
          className="mb-4"
          imageClassName="size-[72px] rounded-full"
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            id="name"
            label="CATEGORY"
            required
            placeholder="Enter category name"
            register={register}
            error={errors.name?.message}
          />

          <div>
            <Label htmlFor="category-description">Description</Label>
            <Textarea
              id="category-description"
              {...register("description")}
              className="mt-1 min-h-[80px] w-full resize-none"
              placeholder="Enter description"
            />
            {errors.description && (
              <p className="text-destructive mt-1 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>FEATURES</Label>
              <Button
                type="button"
                size="sm"
                variant="new_outline"
                onClick={() =>
                  append({ value: "", icon: DEFAULT_CUSTOM_FEATURE_ICON })
                }
              >
                <PlusCircle />
                Add Custom
              </Button>
            </div>

            <div className="mt-2 space-y-2">
              {availableFeatures.map((feature) => {
                const selected = amenities.includes(feature);
                const selectedIcon =
                  featureIconMap[feature] ?? getDefaultFeatureIcon(feature);

                return (
                  <div
                    key={feature}
                    className="flex items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Checkbox
                        id={`feature-${feature}`}
                        checked={selected}
                        onCheckedChange={() => toggleFeature(feature)}
                      />
                      <RoomFeatureIcon icon={selectedIcon} />
                      <Label
                        htmlFor={`feature-${feature}`}
                        className="truncate font-normal"
                      >
                        {feature}
                      </Label>
                    </div>

                    {selected &&
                      renderIconSelect(feature, selectedIcon, (icon) =>
                        setFeatureIcon(feature, icon),
                      )}
                  </div>
                );
              })}

              {fields.map((field, index) => {
                const customFeature = customFeatures[index];
                const feature = normalizeFeatureName(
                  customFeature?.value ?? "",
                );
                const selected = feature ? amenities.includes(feature) : false;
                const selectedIcon =
                  customFeature?.icon ??
                  amenityIcons[feature] ??
                  DEFAULT_CUSTOM_FEATURE_ICON;

                return (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 rounded-md border border-gray-100 px-3 py-2"
                  >
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => toggleFeature(feature)}
                      disabled={!feature}
                    />
                    <Input
                      value={customFeature?.value ?? ""}
                      onChange={(event) =>
                        handleCustomFeatureNameChange(index, event.target.value)
                      }
                      placeholder="Custom feature"
                      className="flex-1"
                    />
                    {renderIconSelect(
                      feature || field.id,
                      selectedIcon,
                      (icon) => setCustomFeatureIcon(index, icon),
                    )}
                    <Button
                      type="button"
                      size="icon_sm"
                      variant="new_ghost"
                      onClick={() => removeCustomFeature(index)}
                      aria-label="Remove custom feature"
                    >
                      <X className="text-custom-destructive-dark" />
                    </Button>
                  </div>
                );
              })}
            </div>

            {errors.amenities && (
              <p className="text-destructive mt-2 text-sm">
                {errors.amenities.message}
              </p>
            )}
          </div>

          <FormField
            id="price"
            label="PRICE"
            type="number"
            placeholder="0"
            required
            register={register}
            error={errors.price?.message}
          />

          <Button
            type="submit"
            variant="new_default"
            size="xl"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Saving..." : "Create Category"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AddCategoryPage;
